/** Express Entry → pre-submission review as an IRCC officer would read the file (D-082):
 * grey areas found by rules, the AI reading of each piece (client's consent), an internal verdict. */

import {
  type Dossier,
  useImmigrationDossiersAiConsentCreate,
  useImmigrationDossiersAiConsentDestroy,
  useImmigrationDossiersOfficerReviewCreate,
  useImmigrationDossiersOfficerReviewReadCreate,
  useImmigrationDossiersOfficerReviewRetrieve,
} from "@agencyos/api-client";
import { Badge, Button, GlassCard, Skeleton } from "@agencyos/ui-web";
import { useQueryClient } from "@tanstack/react-query";
import { FileSearch, ShieldAlert, ShieldCheck, ShieldQuestion } from "lucide-react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { localized } from "../../components/TranslatedFields";

type Severity = "high" | "medium" | "low";
type Verdict = "low_risk" | "medium_risk" | "high_risk";
interface Finding {
  code: string;
  severity: Severity;
  params: Record<string, unknown>;
}
interface Concern {
  severity: Severity;
  area: string;
  finding: string;
  evidence?: string;
  fix: string;
}
interface Saved {
  id: string;
  status: "pending" | "done" | "rules_only" | "rejected";
  findings: Finding[];
  rules_verdict: Verdict;
  extractions: Record<string, unknown>[];
  ai_verdict: { verdict?: Verdict; summary?: string; concerns?: Concern[]; missing_documents?: string[]; officer_questions?: string[] };
  detail: string;
  created_by_name: string;
  reviewed_by_name: string;
  reviewed_at: string | null;
  created_at: string;
}
interface Payload {
  live: { findings: Finding[]; verdict: Verdict };
  latest: Saved | null;
  consent_at: string | null;
}

const TONE = { high: "danger", medium: "offer", low: "default" } as const;
const VERDICT_TONE = { high_risk: "danger", medium_risk: "offer", low_risk: "success" } as const;
const VERDICT_ICON: Record<Verdict, ReactNode> = {
  high_risk: <ShieldAlert size={18} aria-hidden="true" />,
  medium_risk: <ShieldQuestion size={18} aria-hidden="true" />,
  low_risk: <ShieldCheck size={18} aria-hidden="true" />,
};

export function OfficerReview({ dossier }: { dossier: Dossier }) {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const refresh = () =>
    void queryClient.invalidateQueries({ predicate: (q) => String(q.queryKey[0] ?? "").startsWith("/api/v1/immigration/") });
  const review = useImmigrationDossiersOfficerReviewRetrieve(dossier.id, {
    query: {
      staleTime: 0,
      refetchInterval: (query) => {
        const data = query.state.data as { status: number; data: unknown } | undefined;
        const latest = data?.status === 200 ? (data.data as Payload).latest : null;
        return latest?.status === "pending" ? 4000 : false;
      },
    },
  });
  const run = useImmigrationDossiersOfficerReviewCreate({ mutation: { onSuccess: refresh } });
  const read = useImmigrationDossiersOfficerReviewReadCreate({ mutation: { onSuccess: refresh } });
  const consent = useImmigrationDossiersAiConsentCreate({ mutation: { onSuccess: refresh } });
  const withdraw = useImmigrationDossiersAiConsentDestroy({ mutation: { onSuccess: refresh } });
  if (review.isPending) return <Skeleton style={{ height: 240 }} />;
  if (review.data?.status !== 200) return <GlassCard role="alert">{t("common.error")}</GlassCard>;
  const { live, latest, consent_at: consentAt } = review.data.data as unknown as Payload;
  const language = i18n.language === "en" ? "en" : "fr";
  const locale = language === "en" ? "en-GB" : "fr";
  const date = (iso: string) => new Date(iso).toLocaleDateString(locale, { timeZone: "Africa/Douala" });
  const params = (finding: Finding): Record<string, string> => {
    const out: Record<string, string> = {};
    for (const [key, value] of Object.entries(finding.params)) {
      if (key === "person") out[key] = t(`review.person.${String(value)}`);
      else if (key === "item") out[key] = typeof value === "object" && value ? localized(value as Record<string, string>, i18n.language) : String(value);
      else if (key === "member") {
        const member = String(value);
        out[key] = member.startsWith("child") ? t("ee.child", { n: member.slice(5) }) : t(`review.person.${member}`);
      }
      else if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) out[key] = date(value);
      else out[key] = String(value);
    }
    return out;
  };
  const verdict: Verdict = (latest?.status === "done" && latest.ai_verdict.verdict) || live.verdict;

  return (
    <div className="stack">
      <GlassCard as="section" className="stack">
        <div className="section-title">
          <div>
            <h2>
              <FileSearch size={16} aria-hidden="true" /> {t("review.title")}
            </h2>
            <p className="muted">{t("review.intro")}</p>
          </div>
          <Badge tone={VERDICT_TONE[verdict]}>
            {VERDICT_ICON[verdict]} {t(`review.verdict.${verdict}`)}
          </Badge>
        </div>
        <div className="row">
          <Button disabled={run.isPending || latest?.status === "pending"} onClick={() => run.mutate({ id: dossier.id, data: { language } })}>
            {t("review.run")}
          </Button>
          {consentAt ? (
            <>
              <span className="ok">{t("review.consentGiven", { date: date(consentAt) })}</span>
              <Button variant="ghost" onClick={() => withdraw.mutate({ id: dossier.id })}>
                {t("review.withdraw")}
              </Button>
            </>
          ) : (
            <Button variant="secondary" onClick={() => consent.mutate({ id: dossier.id })}>
              {t("review.recordConsent")}
            </Button>
          )}
        </div>
        <p className="check__detail">{consentAt ? t("review.consentNote") : t("review.noConsentNote")}</p>
      </GlassCard>

      <div className="grid">
        <GlassCard as="section" className="stack">
          <h2>{t("review.findings", { count: live.findings.length })}</h2>
          {live.findings.length ? (
            <ul className="checks">
              {live.findings.map((finding, index) => (
                <li key={`${finding.code}-${index}`} className="check check--badge">
                  <Badge tone={TONE[finding.severity]}>{t(`review.severity.${finding.severity}`)}</Badge>
                  <span>
                    <strong>{t(`review.code.${finding.code}.title`, params(finding))}</strong>
                    <span className="check__detail"> · {t(`review.code.${finding.code}.fix`, params(finding))}</span>
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="ok">{t("review.noFinding")}</p>
          )}
        </GlassCard>

        <GlassCard as="section" className="stack">
          <h2>{t("review.aiTitle")}</h2>
          {latest ? (
            <>
              <p className="check__detail">
                {t("review.savedBy", { name: latest.created_by_name || "—", date: date(latest.created_at) })} · {t(`review.status.${latest.status}`)}
              </p>
              {latest.status === "pending" ? <Skeleton style={{ height: 80 }} /> : null}
              {latest.status === "done" ? (
                <>
                  <p>{latest.ai_verdict.summary}</p>
                  {latest.ai_verdict.concerns?.length ? (
                    <ul className="checks">
                      {latest.ai_verdict.concerns.map((concern, index) => (
                        <li key={index} className="check check--badge">
                          <Badge tone={TONE[concern.severity]}>{t(`review.severity.${concern.severity}`)}</Badge>
                          <span>
                            <strong>
                              {concern.area} · {concern.finding}
                            </strong>
                            {concern.evidence ? <span className="check__detail"> · {concern.evidence}</span> : null}
                            <br />
                            <span className="check__detail">
                              {t("review.fix")} : {concern.fix}
                            </span>
                          </span>
                        </li>
                      ))}
                    </ul>
                  ) : null}
                  {latest.ai_verdict.officer_questions?.length ? (
                    <>
                      <h3>{t("review.questions")}</h3>
                      <ul>
                        {latest.ai_verdict.officer_questions.map((question) => (
                          <li key={question}>{question}</li>
                        ))}
                      </ul>
                    </>
                  ) : null}
                </>
              ) : null}
              {latest.status === "rules_only" || latest.status === "rejected" ? (
                <p className="muted">{t(`review.fallback.${latest.status}`)}</p>
              ) : null}
              {latest.extractions.length ? (
                <>
                  <h3>{t("review.pieces")}</h3>
                  <table className="table">
                    <tbody>
                      {latest.extractions.map((piece, index) => (
                        <tr key={index}>
                          <td>{String(piece.item ?? "").replace(/_/g, " ")}</td>
                          <td className="muted">{String(piece.document_type ?? piece.skipped ?? "")}</td>
                          <td>{Array.isArray(piece.issues) && piece.issues.length ? (piece.issues as string[]).join(" · ") : t("review.noIssue")}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              ) : null}
              {latest.reviewed_at ? (
                <p className="ok">{t("review.read", { name: latest.reviewed_by_name, date: date(latest.reviewed_at) })}</p>
              ) : (
                <div className="row">
                  <Button variant="secondary" disabled={read.isPending || latest.status === "pending"} onClick={() => read.mutate({ id: dossier.id })}>
                    {t("review.markRead")}
                  </Button>
                  <span className="check__detail">{t("review.readNote")}</span>
                </div>
              )}
            </>
          ) : (
            <p className="muted">{t("review.none")}</p>
          )}
        </GlassCard>
      </div>
      <p role="note" className="check__detail">
        {t("review.disclaimer")}
      </p>
    </div>
  );
}
